import splash from "assets/images/splash.png"
import axios from "axios"
import {
  desktop,
  desktopContentPadding,
  mobile,
  space,
  tablet,
  tabletContentPadding,
} from "components/_blueprint/Layout"
import { Button } from "components/atoms"
import { Loader, LoaderTheme } from "components/atoms/Loader"
import { Notifications } from "components/atoms/Notifications"
import { useApplicationSummaryEnhanced } from "hooks/useApplicationSummaryEnhanced"
import { useTrackNavigatedToPage } from "hooks/useTrackPageView"
import { useSession } from "lib/auth/react"
import { ToCurrency } from "lib/CurrencyFormatter"
import { DateDiff } from "lib/Date"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { LogError } from "logging"
import { ApplicationSummaryEnhanced } from "models/ApplicationSummaryEnhanced"
import { EventAction, EventCategory, EventLabel } from "models/GoogleAnalytics"
import * as React from "react"
import { useEffect, useState } from "react"
import toast from "react-hot-toast/headless"
import { useParams } from "react-router-dom"
import styled from "styled-components"
import devices from "styles/devices"
import { MY_MOJO_API } from "../../../../Config"
import {
  DisclaimerFooter,
  HowItWorksRateCheckPromise,
  RateCheckPromiseFaqs,
  RateCheckPromiseHero,
} from "../../../RateCheckPromise"
import { IdentifiedLowerRate, ILRState, RateCheckBody, RateCheckInfo } from "../types"
import { FinalRateCard, RateCard } from "./RateCard"
import { RateCheckHeader } from "./RateCheckHeader"
import { RateCheckSummary } from "./RateCheckSummary"
import { NewRateModal } from "./RateSelectedToast"
import Refresh from "url:assets/images/icons/Refresh.svg"

const shouldShowCompletionWarning = ({
  cohort,
  completionDate,
}: ApplicationSummaryEnhanced): boolean => {
  // Only show the warning to purchase customers who are less than 4 weeks away from their completion date, if one is set
  return (
    cohort === "Purchase" && !!completionDate && DateDiff(completionDate, new Date(), "week") < 4
  )
}

const RateMissingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 12px;
  background: #f3f5fc;
  padding: 24px;
  text-align: center;
`

const RateMissingTitle = styled.div`
  font-size: 16px;
`

const RefreshIcon = styled.div`
  ::before {
    content: url(${Refresh});
  }
`

export const RateCheckerPage = ({ applicationId }: { applicationId: string }) => {
  const { friendlyId } = useParams<{ friendlyId: string }>()
  const { application } = useApplicationSummaryEnhanced({ friendlyId })
  const { isLoading: sessionLoading } = useSession()
  const [rateCheckerData, setRateCheckerData] = useState<RateCheckBody | null>(null)
  const [actionClicked, setActionClicked] = useState<"current" | "new" | null>(null)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showNewRateModal, setShowNewRateModal] = useState(false)
  const [warningChecked, setWarningChecked] = useState(false)
  const showCompletionWarning = !!application && shouldShowCompletionWarning(application)
  useTrackNavigatedToPage("/application/rate-check-promise")

  useEffect(() => {
    if (applicationId && !sessionLoading) {
      axios
        .get(`${process.env.MY_MOJO_API}/rate-check/${applicationId}/v2`, {
          withCredentials: true,
        })
        .then(({ data }) => {
          setRateCheckerData(data.data)
          setIsError(false)
          setIsLoading(false)
        })
        .catch((error) => {
          console.error(error)
          setIsError(true)
          setIsLoading(false)
        })
    }
  }, [applicationId, sessionLoading])

  if (isLoading || !rateCheckerData) {
    return (
      <ContentGrid>
        <ContentContainer>
          <LoaderContainer>
            <Loader size={150} theme={LoaderTheme.Outline} />
          </LoaderContainer>
        </ContentContainer>
      </ContentGrid>
    )
  }

  const { rateCheck, identifiedLowerRate } = rateCheckerData
  if (!rateCheck) {
    return (
      <ContentGrid>
        <ContentContainer>
          <RateCheckPromiseHero />
          <HowItWorksRateCheckPromise />
          <RateCheckPromiseFaqs />
          <DisclaimerFooter />
        </ContentContainer>
      </ContentGrid>
    )
  }

  const arrows = fetchArrows(rateCheck, identifiedLowerRate)

  const handleCheckboxClick = () => setWarningChecked((val) => !val)

  const handleRateClick = (rateClicked: "current" | "new") => () => {
    const currentRate = rateClicked === "current"
    setActionClicked(rateClicked)
    LogGaEvent({
      action: EventAction.buttonClicked,
      event_category: EventCategory.functionalInteraction,
      event_label: currentRate ? EventLabel.autoRateDecline : EventLabel.autoRateAccept,
    })
    if (identifiedLowerRate) {
      axios
        .post(
          `${process.env.MY_MOJO_API}/rate-check/update`,
          {
            id: identifiedLowerRate.id,
            status: currentRate ? ILRState.Declined : ILRState.Progressing,
          },
          {
            withCredentials: true,
          }
        )
        .catch((error) => {
          console.error(error)
          setIsError(true)
        })
    }
    if (rateClicked === "current") {
      toast(
        <div>
          <div>
            <b>Thanks for letting us know</b>
          </div>
          <div>
            You’ll stay on your current rate and we’ll keep you updated if the rate goes down again
          </div>
        </div>,
        { id: "confirmed" }
      )
    }

    if (rateClicked === "new") {
      setShowNewRateModal(true)
    }
  }

  const completionCutoff = rateCheck.status === "stopped-completion-cutoff"

  return (
    <ContentGrid>
      <ContentContainer>
        <>
          <RateCheckHeader rateCheck={rateCheck} identifiedLowerRate={identifiedLowerRate} />
          <RatesWrapper bg={completionCutoff}>
            <RateCheckSummary rateCheck={rateCheck} identifiedLowerRate={identifiedLowerRate} />
            <RatesContainer finalRate={completionCutoff}>
              {completionCutoff ? (
                <FinalRateCard
                  productDetails={rateCheckerData.rateCheck.customerProductDetails}
                  productInput={rateCheckerData.rateCheck.productInput}
                />
              ) : (
                <>
                  <RateCard
                    actionClicked={actionClicked}
                    currentRate={true}
                    currentInitialCost={rateCheck.customerProductDetails.TrueCostOverInitialPeriod}
                    identifiedLowerRateId={identifiedLowerRate?.id}
                    identifiedLowerRateStatus={identifiedLowerRate?.status}
                    isError={isError}
                    productDetails={
                      identifiedLowerRate?.status === ILRState.Completed
                        ? identifiedLowerRate.productDetails // if the ILR was successfully completed, show the new rate as the current rate
                        : rateCheckerData.rateCheck.customerProductDetails // if the ILR was successfully completed, show the new rate as the current rate
                    }
                    productInput={rateCheckerData.rateCheck.productInput}
                    setIsError={setIsError}
                    setActionClicked={setActionClicked}
                    handleEsisDownload={() => {
                      handleEsisDownloadCurrRate(applicationId)
                    }}
                    arrows={arrows}
                    handleClick={handleRateClick("current")}
                    warningChecked={false}
                    handleWarningCheckboxChange={() => ({})}
                  />
                  {identifiedLowerRate ? (
                    <RateCard
                      actionClicked={actionClicked}
                      currentRate={false}
                      currentInitialCost={
                        rateCheck.customerProductDetails.TrueCostOverInitialPeriod
                      }
                      identifiedLowerRateId={identifiedLowerRate.id}
                      identifiedLowerRateStatus={identifiedLowerRate.status}
                      isError={isError}
                      productDetails={identifiedLowerRate.productDetails}
                      productInput={rateCheckerData.rateCheck.productInput}
                      setActionClicked={setActionClicked}
                      setIsError={setIsError}
                      handleEsisDownload={() => {
                        handleEsisDownloadNewRate(applicationId, friendlyId, identifiedLowerRate.id)
                      }}
                      arrows={arrows}
                      handleClick={handleRateClick("new")}
                      showCompletionDateWarning={showCompletionWarning}
                      warningChecked={warningChecked}
                      handleWarningCheckboxChange={handleCheckboxClick}
                    />
                  ) : (
                    <RateMissingContainer>
                      <RefreshIcon />
                      <RateMissingTitle>
                        When we find a lower rate with {rateCheck.customerProductDetails.LenderName}
                        , it’ll appear here
                      </RateMissingTitle>
                      <div>Don’t worry, we’ll email to let you know</div>
                    </RateMissingContainer>
                  )}
                </>
              )}
            </RatesContainer>
          </RatesWrapper>
          <>
            <HowItWorksRateCheckPromise />
            <RateCheckPromiseFaqs />
            <DisclaimerFooter />
          </>
          <Notifications notificationComponent={<RateSelectedToast />} />

          {showNewRateModal && <NewRateModal onClose={() => setShowNewRateModal(false)} />}
        </>
      </ContentContainer>
    </ContentGrid>
  )
}

export type ArrowColor = "green" | "orange" | null

export type Arrows = {
  initialRate: ArrowColor
  monthlyPayment: ArrowColor
  overallCost: ArrowColor
  aprc: ArrowColor
  productFee: ArrowColor
  endDate: ArrowColor
}
const ToastText = styled.div`
  width: 100%;

  @media screen and ${devices.bp_sm} {
    max-width: 70%;
  }
`

const ToastButton = styled(Button)`
  width: 100%;
  background: #1a3dd1;
  color: #fff;
  text-align: center;
  box-shadow: inset 0 -6px 0px #052ea3;

  @media screen and ${devices.bp_sm} {
    width: auto;
  }
`

const RateSelectedToast = ({ id, message }: { id?: string; message?: string }) => {
  return (
    <React.Fragment>
      <ToastText>{message}</ToastText>
      <ToastButton
        onClick={() => toast.dismiss(id)}
        width="auto"
        type={2}
        text="Got it"
        style={{
          alignSelf: "end",
        }}
      />
    </React.Fragment>
  )
}

const handleEsisDownloadCurrRate = async (applicationId: string) => {
  LogGaEvent({
    action: EventAction.rateCheckEsisDownloadClicked,
    event_category: EventCategory.siteInteraction,
    event_label: "Rate check download current rate ESIS clicked",
  })

  return axios
    .get(`${process.env.MY_MOJO_API}/recommendation/${applicationId}`, {
      withCredentials: true,
    })
    .then(({ data }) => data.esisS3Key)
    .then((esisS3Key) =>
      axios.post(
        `${MY_MOJO_API}/documents/download-url`,
        {
          applicationId,
          s3Key: esisS3Key,
        },
        {
          withCredentials: true,
        }
      )
    )
    .then((response) => {
      if (response.status !== 200) throw Error("Failed to download ILR current rate ESIS document")
      LogGaEvent({
        action: EventAction.rateCheckEsisDownloadResult,
        event_category: EventCategory.customEvent,
        event_label: "Download current rate ESIS success",
      })
      window.location.assign(response.data.downloadUrl)
    })
    .catch((err) => {
      LogError(err)
    })
}

const handleEsisDownloadNewRate = async (
  applicationId: string,
  friendlyId: string,
  ilrRateId: string
) => {
  LogGaEvent({
    action: EventAction.rateCheckEsisDownloadClicked,
    event_category: EventCategory.siteInteraction,
    event_label: "Rate check download new rate ESIS clicked",
  })

  return axios
    .post(
      `${MY_MOJO_API}/documents/download-url`,
      {
        applicationId,
        s3Key: `Opportunities/${friendlyId}/rate-check/ilr/${ilrRateId}.pdf`,
      },
      {
        withCredentials: true,
      }
    )
    .then((response) => {
      if (response.status !== 200) throw Error("Failed to download ILR new rate ESIS document")
      LogGaEvent({
        action: EventAction.rateCheckEsisDownloadResult,
        event_category: EventCategory.customEvent,
        event_label: "Download new rate ESIS success",
      })
      window.open(response.data.downloadUrl)
    })
    .catch((err) => {
      LogError(err)
    })
}

function fetchArrows(rateCheck: RateCheckInfo, identifiedLowerRate: IdentifiedLowerRate | null) {
  if (!identifiedLowerRate)
    return {
      initialRate: null,
      monthlyPayment: null,
      overallCost: null,
      aprc: null,
      productFee: null,
      endDate: null,
    }

  const { InitialMonthlyPayment, InitialMonthlyPaymentIncludingFees } =
    rateCheck.customerProductDetails || {}

  const { addFeesToLoan } = rateCheck.productInput || {}

  const ilrMonthlyPayment = addFeesToLoan
    ? ToCurrency(identifiedLowerRate.productDetails.InitialMonthlyPaymentIncludingFees)
    : ToCurrency(identifiedLowerRate.productDetails.InitialMonthlyPayment)

  const monthlyPayment = addFeesToLoan
    ? ToCurrency(InitialMonthlyPaymentIncludingFees)
    : ToCurrency(InitialMonthlyPayment)

  const compareValues = (currentValue: number | string, newValue: number | string): ArrowColor => {
    return newValue < currentValue ? "green" : newValue > currentValue ? "orange" : null
  }

  const compareDates = (currentValue: string, newValue: string): ArrowColor => {
    const currentValueMs = new Date(currentValue).getTime()
    const valueMs = new Date(newValue).getTime()
    return valueMs > currentValueMs ? "orange" : null
  }

  return {
    initialRate: compareValues(
      rateCheck.customerProductDetails.InitialPayRate,
      identifiedLowerRate.productDetails.InitialPayRate
    ),
    monthlyPayment: compareValues(monthlyPayment, ilrMonthlyPayment),
    overallCost: compareValues(
      rateCheck.customerProductDetails.TrueCostOverInitialPeriod,
      identifiedLowerRate.productDetails.TrueCostOverInitialPeriod
    ),
    aprc: compareValues(
      rateCheck.customerProductDetails.AprLenders,
      identifiedLowerRate.productDetails.AprLenders
    ),
    productFee: compareValues(
      rateCheck.customerProductDetails.ArrangementFee,
      identifiedLowerRate.productDetails.ArrangementFee
    ),
    endDate: compareDates(
      rateCheck.customerProductDetails.InitialRatePeriodPrecise,
      identifiedLowerRate.productDetails.InitialRatePeriodPrecise
    ),
  }
}

const ContentGrid = styled.div({
  display: "grid",
  gridColumnStart: 2,
  gridRowStart: 1,
  columnGap: space,
  [mobile]: {
    gridTemplateColumns: "repeat(3, 1fr)",
    padding: "1rem",
  },
  [tablet]: {
    gridTemplateColumns: "repeat(6, 1fr)",
    padding: tabletContentPadding,
  },
  [desktop]: {
    gridTemplateColumns: "repeat(12, 1fr)",
    padding: `${desktopContentPadding}px ${40}px`,
  },
})

const ContentContainer = styled.div({
  gridColumnStart: 1,
  [mobile]: {
    gridColumnEnd: "span 3",
  },
  [tablet]: {
    gridColumnEnd: "span 6",
  },
  [desktop]: {
    gridColumnStart: 2,
    gridColumnEnd: "span 10",
  },
})

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  @media screen and ${devices.bp_xl} {
    width: 34em;
  }
`

const RatesWrapper = styled.div<{ bg: boolean }>(({ bg }) => ({
  display: "flex",
  flexDirection: "column",
  background: "#f1f4fe",
  padding: "16px",
  borderRadius: "32px",
  gap: "16px",
  backgroundImage: bg
    ? `linear-gradient(rgba(241,244,254,0.8), rgba(241,244,254,0.8)), url(${splash})`
    : "none",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  alignItems: "center",
}))

const RatesContainer = styled.div<{ finalRate: boolean }>(({ finalRate }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "16px",
  [desktop]: {
    flexDirection: "row",
    width: finalRate ? "70%" : "100%",
  },
}))
